import React, { useCallback, useEffect, useRef, useState } from 'react'
import { BrowserRouter, Routes, Route } from 'react-router'
import type {
  InboxConversationSummary,
  ReviewerDisplayState
} from './inboxTypes'
import { fetchInboxConversations } from './services/fetchInboxConversations'
import { InboxPageView } from './InboxPageView'

type LoadState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'success', conversations: InboxConversationSummary[] }

interface InboxBaseProps {
  reviewerDisplayState?: ReviewerDisplayState
}

const InboxBaseContent: React.FC<InboxBaseProps> = ({ reviewerDisplayState = 'ALL_VISIBLE' }) => {
  const [state, setState] = useState<LoadState>({ status: 'loading' })
  const isMountedRef = useRef(true)
  const locallyReadConversationIdsRef = useRef<Set<number>>(new Set())

  const applyLocalReadOverrides = useCallback(
    (summaries: InboxConversationSummary[]): InboxConversationSummary[] =>
      summaries.map((summary) => (
        locallyReadConversationIdsRef.current.has(summary.id)
          ? { ...summary, unreadMessages: false }
          : summary
      )),
    []
  )

  const refreshConversations = useCallback(async (): Promise<void> => {
    try {
      const summaries = await fetchInboxConversations()
      if (!isMountedRef.current) return
      const withLocalReadApplied = applyLocalReadOverrides(summaries)
      setState({ status: 'success', conversations: withLocalReadApplied })
    } catch {
      if (!isMountedRef.current) return
      setState({ status: 'error' })
    }
  }, [applyLocalReadOverrides])

  useEffect(() => {
    isMountedRef.current = true
    void refreshConversations()

    return () => {
      isMountedRef.current = false
    }
  }, [refreshConversations])

  if (state.status === 'loading') {
    return (
      <div className='inbox-state inbox-state--loading'>
        Loading…
      </div>
    )
  }

  if (state.status === 'error') {
    return (
      <div className='inbox-state inbox-state--error'>
        Unable to load inbox.
      </div>
    )
  }

  const handleAfterSendMessage = async (_text: string): Promise<void> => {
    await refreshConversations()
  }
  const handleConversationOpened = async (conversationId: number): Promise<void> => {
    locallyReadConversationIdsRef.current.add(conversationId)
    setState((prevState) => {
      if (prevState.status !== 'success') return prevState
      return {
        status: 'success',
        conversations: prevState.conversations.map((conversation) => (
          conversation.id === conversationId
            ? { ...conversation, unreadMessages: false }
            : conversation
        ))
      }
    })
    await refreshConversations()
  }

  return (
    <Routes>
      <Route
        path='/'
        element={
          <InboxPageView
            conversations={state.conversations}
            reviewerDisplayState={reviewerDisplayState}
            onAfterSendMessage={handleAfterSendMessage}
            onConversationOpened={handleConversationOpened}
          />
        }
      />
      <Route
        path='/:conversationId'
        element={
          <InboxPageView
            conversations={state.conversations}
            reviewerDisplayState={reviewerDisplayState}
            onAfterSendMessage={handleAfterSendMessage}
            onConversationOpened={handleConversationOpened}
          />
        }
      />
    </Routes>
  )
}

export const InboxBase: React.FC<InboxBaseProps> = (props) => {
  return (
    <BrowserRouter basename='/inbox'>
      <InboxBaseContent {...props} />
    </BrowserRouter>
  )
}
